import customCheckboxRadioSwitchStyles from 'assets/jss/material-dashboard-pro-react/customCheckboxRadioSwitch';
import { useAdCampaignAdPreview, useCreateAdCampaign } from 'data/ad-campaign';
import { useCampaigns } from 'data/campaign';
import {
  Field, FieldArray, FieldProps, Form, Formik, FormikErrors, FormikProps,
} from 'formik';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CreateAdCampaignInput,
  EAdCreativeMediaType,
  EAdCreativeSource,
} from 'types/ad-campaign';
import { EAgentLandingPageType } from 'types/agent-landing-page';
import { ECampaignStatus } from 'types/campaign';
import * as Yup from 'yup';

import {
  Box, ButtonBase, Chip, FormControlLabel, Grid, Radio, Slider, TextField,
  Tooltip, Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LibraryAddCheckRoundedIcon from '@material-ui/icons/LibraryAddCheckRounded';
import { Autocomplete } from '@material-ui/lab';

import Button from 'creative-components/CustomButtons/Button';
import CustomInputDashboard from 'creative-components/CustomInput/CustomInputDashboard';

import ActionModal from 'components/ActionModal/ActionModal';
import AdCreativeUploader from 'components/AdCreativeUploader';
import AdPreview from 'components/AdPreview';
import { ADS_DASHBOARD_ROUTE, useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import DashboardSection from 'components/DashboardSection/DashboardSection';
import DataMap from 'components/DataMap/DataMap';
import FormikOnError from 'components/FormikOnError/FormikOnError';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import {
  AD_CREATIVE_PROCESSING_LOADING_MODAL,
  useLoadingIndicatorContext,
} from 'components/LoadingIndicator/LoadingIndicatorProvider';
import MultiZipcodesInput from 'components/MultiZipcodesInput/MultiZipcodesInput';
import AddPaymentMethodButton from 'components/PaymentMethods/AddPaymentMethodButton';
import PaymentMethodSelector from 'components/PaymentMethods/PaymentMethodSelector';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import analyticsClient from 'utils/analytics';
import {
  formatCents,
  getAgentLandingPageSampleUrlFromType,
  showAPIErrorAlert,
  urlRegExp,
} from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  ...customCheckboxRadioSwitchStyles(theme),
  map: {
    height: '95vh',
    maxHeight: '800px',
    display: 'flex',
    flexDirection: 'column',
    margin: '20px 0 40px',
    '& > div:first-child': {
      marginBottom: '20px',
    },
  },
  settingInput: {
    width: '100%',
    maxWidth: '440px',
  },
  autoCompleteSelectText: {
    fontWeight: 500,
  },
  spacerSmall: {
    marginTop: '70px',
  },
  labelText: {
    color: theme.palette.darkGray.main,
    fontSize: '16px',
    lineHeight: '20px',
    marginBottom: '6px',
    fontWeight: 600,
  },
  helperText: {
    marginTop: '6px',
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: 400,
    color: theme.palette.lightGray.main,
  },
  landingPageInfo: {
    marginTop: '6px',
    fontSize: '15px',
    lineHeight: '22px',
    fontWeight: 400,
    color: theme.palette.lightGray.main,
    '& a': {
      color: theme.palette.orange.main,
      textDecoration: 'underline',
    },
  },
  buildLayout: {
    marginTop: '26px',
  },
  buildPanel: {
    border: `1px solid ${theme.palette.grayScale4.main}`,
    borderRadius: '12px',
    padding: '24px',
    backgroundColor: theme.palette.white.main,
    marginBottom: '24px',
    [theme.breakpoints.down('xs')]: {
      padding: '18px',
    },
  },
  panelTitle: {
    color: theme.palette.darkGray.main,
    fontSize: '20px',
    lineHeight: '26px',
    fontWeight: 700,
    marginBottom: '6px',
  },
  panelDescription: {
    color: theme.palette.lightGray.main,
    fontSize: '15px',
    lineHeight: '22px',
    marginBottom: '18px',
  },
  sourceChoices: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '14px',
    marginBottom: '18px',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  sourceChoice: {
    position: 'relative',
    minHeight: '118px',
    width: '100%',
    border: `2px solid ${theme.palette.grayScale4.main}`,
    borderRadius: '10px',
    padding: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'left',
    transition: 'border-color 180ms ease, background-color 180ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.orange.main}`,
      outlineOffset: '2px',
    },
    '& > svg:first-child': {
      color: theme.palette.primary.main,
      marginBottom: '8px',
    },
  },
  sourceChoiceSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.offWhite.main,
  },
  selectedIcon: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    color: `${theme.palette.primary.main} !important`,
  },
  sourceTitle: {
    color: theme.palette.darkGray.main,
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '21px',
  },
  sourceDescription: {
    color: theme.palette.lightGray.main,
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '4px',
  },
  destinationOptions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  renewalSummary: {
    marginTop: '14px',
    padding: '12px 14px',
    borderRadius: '8px',
    backgroundColor: theme.palette.offWhite.main,
    color: theme.palette.darkGray.main,
    fontSize: '15px',
    lineHeight: '22px',
  },
  previewPanel: {
    position: 'sticky',
    top: '24px',
  },
  previewFrame: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '350px',
    borderRadius: '10px',
    backgroundColor: theme.palette.offWhite.main,
    padding: '12px',
    overflow: 'hidden',
  },
  desktopPreview: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mobilePreview: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      marginTop: '20px',
    },
  },
  simpleViewNextButton: {
    float: 'right',
    marginTop: '30px',
    marginBottom: '20px',
  },
}));

interface FormValues {
  name: string;
  useCustomCallToActionLink: boolean;
  customCallToActionLink: string;
  budget: number;
  zipCodes: string[];
  customAudienceCampaigns: any[];
  paymentMethodId: string;
  creativeSource: EAdCreativeSource;
  creativeMediaType: EAdCreativeMediaType;
  creativeFile?: File;
}

const maxNumberOfZipcodes = 10;
const budgetMinimum = 20000;
const budgetMaximum = 200000;

const showValidationErrorToasts = (
  errors: FormikErrors<FormValues>,
  values: FormValues,
) => {
  const validationErrors: { id: string; message: string }[] = [];

  if (errors.zipCodes) {
    validationErrors.push({
      id: 'farm-area',
      message: values.zipCodes.length > maxNumberOfZipcodes
        ? `Choose no more than ${maxNumberOfZipcodes} farm areas for your ad audience.`
        : 'Choose at least one farm area for your ad audience.',
    });
  }

  if (errors.creativeFile) {
    validationErrors.push({
      id: 'creative',
      message: values.creativeMediaType === EAdCreativeMediaType.Image
        ? 'Choose a valid custom image and confirm its square crop before creating your ad.'
        : 'Choose a valid custom video before creating your ad.',
    });
  }

  if (errors.name) {
    validationErrors.push({
      id: 'name',
      message: values.name.trim()
        ? 'Keep the internal ad name to 64 characters or fewer.'
        : 'Enter an internal ad name.',
    });
  }

  if (values.useCustomCallToActionLink && errors.customCallToActionLink) {
    const destinationWithoutProtocol = values.customCallToActionLink
      .replace(/^https?:\/\//i, '')
      .trim();
    validationErrors.push({
      id: 'destination',
      message: destinationWithoutProtocol
        ? 'Enter a valid destination URL for your website or custom landing page.'
        : 'Enter a destination URL for your website or custom landing page.',
    });
  }

  if (errors.budget) {
    validationErrors.push({
      id: 'budget',
      message: `Choose a monthly ad budget between ${formatCents(budgetMinimum, 0)} and ${formatCents(budgetMaximum, 0)}.`,
    });
  }

  if (errors.creativeSource || errors.creativeMediaType) {
    validationErrors.push({
      id: 'creative-selection',
      message: 'Choose how you want to create your ad and select an image or video.',
    });
  }

  if (!validationErrors.length) {
    validationErrors.push({
      id: 'form',
      message: 'Review the highlighted fields before creating your ad.',
    });
  }

  validationErrors.forEach(({ id, message }) => {
    toast.error(message, {
      id: `create-ad-validation-${id}`,
      duration: 6000,
    });
  });
};

type Props = {
  onSimpleViewSubmit?: (data: CreateAdCampaignInput) => void;
};

export default ({ onSimpleViewSubmit }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const { userInfo } = useUserInfoContext();
  const { user } = useAuthDataContext();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any;
  const { createAdCampaign } = useCreateAdCampaign();

  const [selectedCreativeSource, setSelectedCreativeSource] = React.useState(EAdCreativeSource.HarvistGenerated);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = React.useState<string>();
  const [uploadProgress, setUploadProgress] = React.useState<number>();
  const [uploadError, setUploadError] = React.useState<string>();
  const [showPaymentMethodModal, setShowPaymentMethodModal] = React.useState(false);

  const { campaigns, isLoadingCampaigns } = useCampaigns(
    undefined,
    [ECampaignStatus.Active],
    false,
    !!user,
  );
  const { adPreview, isLoadingAdPreview } = useAdCampaignAdPreview(
    selectedCreativeSource === EAdCreativeSource.HarvistGenerated,
  );

  if (!!user && !userInfo) return <LoadingIndicator />;

  const renderPreview = (values: FormValues) => {
    const isUpload = values.creativeSource === EAdCreativeSource.UserUpload;
    const requiresMarketingProfile = !values.useCustomCallToActionLink || !isUpload;

    return (
      <div className={classes.previewFrame}>
        <AdPreview
          mediaUrl={isUpload ? uploadedPreviewUrl : adPreview}
          mediaType={isUpload ? values.creativeMediaType : EAdCreativeMediaType.Image}
          isLoading={!isUpload && isLoadingAdPreview}
          showMarketingProfileNotice={requiresMarketingProfile}
          alt={isUpload ? 'Preview of uploaded ad creative' : 'Preview of Harvist-generated ad creative'}
        />
      </div>
    );
  };

  return (
    <Formik<FormValues>
      initialValues={{
        name: '',
        useCustomCallToActionLink: false,
        customCallToActionLink: '',
        budget: budgetMinimum,
        zipCodes: [],
        customAudienceCampaigns: [],
        paymentMethodId: '',
        creativeSource: EAdCreativeSource.HarvistGenerated,
        creativeMediaType: EAdCreativeMediaType.Image,
        creativeFile: undefined,
      }}
      onSubmit={async (values) => {
        const {
          name,
          useCustomCallToActionLink,
          customCallToActionLink,
          budget,
          zipCodes,
          customAudienceCampaigns,
          paymentMethodId,
          creativeSource,
          creativeMediaType,
          creativeFile,
        } = values;

        if (!zipCodes.length) {
          toast.warning('Please enter zip codes for your digital ad\'s audience!');
          return;
        }
        if (creativeSource === EAdCreativeSource.UserUpload && !creativeFile) {
          toast.warning('Upload and confirm your ad creative before continuing.');
          return;
        }

        const createAdCampaignData: CreateAdCampaignInput = {
          name,
          useCustomCallToActionLink,
          customCallToActionLink,
          budget,
          customAudienceCampaignIds: customAudienceCampaigns.map((campaign: any) => campaign._id),
          zipCodes,
          paymentMethodId,
          creative: creativeSource === EAdCreativeSource.UserUpload ? {
            source: EAdCreativeSource.UserUpload,
            mediaType: creativeMediaType,
            file: creativeFile!,
          } : {
            source: EAdCreativeSource.HarvistGenerated,
          },
        };

        if (onSimpleViewSubmit) {
          onSimpleViewSubmit(createAdCampaignData);
          return;
        }
        if (!paymentMethodId) {
          setShowPaymentMethodModal(true);
          return;
        }

        showLoadingIndicatorModal(
          creativeSource === EAdCreativeSource.UserUpload
            ? AD_CREATIVE_PROCESSING_LOADING_MODAL
            : undefined,
        );
        setShowPaymentMethodModal(false);
        setUploadError(undefined);
        setUploadProgress(
          creativeSource === EAdCreativeSource.UserUpload ? 0 : undefined,
        );

        try {
          const adCampaignId = await createAdCampaign(createAdCampaignData, {
            onUploadProgress: setUploadProgress,
          });

          await analyticsClient.track('ad_campaign_creation_completed', {
            agentdata_user_id: user.id,
            role: user.role,
            brokerage_id: userInfo.agentInfo.brokerage?._id ?? null,
            creative_source: creativeSource,
            creative_media_type: creativeMediaType,
          });

          history.push(`${ADS_DASHBOARD_ROUTE}/${adCampaignId}`);
          toast.success('Your ad has been created! Pending ad network approval, your ad will start running soon.');
        } catch (err) {
          console.error('error', err);
          if (creativeSource === EAdCreativeSource.UserUpload) {
            const errorMessage = (err as any)?.response?.data?.error
              ?? (err instanceof Error ? err.message : 'The creative upload failed. Please try again.');
            setUploadError(errorMessage);
            toast.error(errorMessage, {
              id: 'create-ad-creative-upload-error',
              duration: 6000,
            });
          } else {
            showAPIErrorAlert(err);
          }
        } finally {
          hideLoadingIndicatorModal();
        }
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().max(64).required(),
        useCustomCallToActionLink: Yup.boolean().required(),
        customCallToActionLink: Yup.string().url().matches(urlRegExp)
          .when('useCustomCallToActionLink', {
            is: true,
            then: (schema) => schema.max(128).required(),
            otherwise: (schema) => schema.notRequired(),
          }),
        budget: Yup.number().integer().min(budgetMinimum).max(budgetMaximum)
          .required(),
        zipCodes: Yup.array().of(Yup.string()).min(1).max(maxNumberOfZipcodes)
          .required(),
        customAudienceCampaigns: Yup.array().of(Yup.object()).required(),
        creativeSource: Yup.mixed().oneOf(Object.values(EAdCreativeSource)).required(),
        creativeMediaType: Yup.mixed().oneOf(Object.values(EAdCreativeMediaType)).required(),
        creativeFile: Yup.mixed().when('creativeSource', {
          is: EAdCreativeSource.UserUpload,
          then: (schema) => schema.required('Upload and confirm your creative.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        paymentMethodId: Yup.string(),
      })}
    >
      {({
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        values,
        handleSubmit,
        errors,
        touched,
      }: FormikProps<FormValues>) => (
        <Form>
          <FormikOnError onInvalidSubmit={showValidationErrorToasts}>
            {showPaymentMethodModal && (
              <ActionModal
                maxWidth="sm"
                backgroundColor={theme.palette.offWhite.main}
                textColor={theme.palette.darkGray.main}
                icon={<LibraryAddCheckRoundedIcon />}
                onClose={() => setShowPaymentMethodModal(false)}
                title="Start Your Ad"
                message={(
                  <Box width="100%" maxWidth="400px">
                    Choose a payment method before we upload your creative and create the ad.
                    <br />
                    <br />
                    <Field name="paymentMethodId">
                      {({ field }: FieldProps) => (
                        <PaymentMethodSelector inputProps={{ ...field }} />
                      )}
                    </Field>
                    <br />
                    <AddPaymentMethodButton center />
                    <br />
                  </Box>
                )}
                buttons={[
                  <ButtonBase disabled={!values.paymentMethodId} onClick={() => handleSubmit()}>
                    Create My Ad
                  </ButtonBase>,
                ]}
              />
            )}

            {!onSimpleViewSubmit && <DashboardSection sectionName="Create Your Digital Ad" />}

            <FieldArray
              name="zipCodes"
              render={(arrayHelpers) => (
                <div className={classes.map}>
                  <MultiZipcodesInput
                    fieldName={arrayHelpers.name}
                    labelText="Type zip codes you want your digital ad to target and press enter"
                    arrayHelpers={arrayHelpers}
                    maxNumberOfZipcodes={maxNumberOfZipcodes}
                  />
                  <DataMap
                    highlightZipcodes={values.zipCodes}
                    zipcodesFormLabel="Select the Zip Codes you want your ad to target"
                  />
                  <div className={classes.helperText}>
                    Our ad partners target audiences within a 15-mile radius of the zip codes selected.
                  </div>
                </div>
              )}
            />

            <FieldArray
              name="customAudienceCampaigns"
              render={() => (
                <Autocomplete
                  style={{ maxWidth: '600px' }}
                  classes={{
                    loading: classes.autoCompleteSelectText,
                    option: classes.autoCompleteSelectText,
                    noOptions: classes.autoCompleteSelectText,
                  }}
                  loading={isLoadingCampaigns && !!user}
                  disabled={!user}
                  multiple
                  filterSelectedOptions
                  value={values.customAudienceCampaigns}
                  onChange={(event, newValue) => setFieldValue('customAudienceCampaigns', newValue, true)}
                  options={campaigns ?? []}
                  renderTags={(tags, getTagProps) => tags.map((campaign, index) => (
                    <Chip
                      color="secondary"
                      key={campaign._id}
                      label={`${campaign.name} (${campaign.subscribedEvents.length} Prospects)`}
                      {...getTagProps({ index })}
                    />
                  ))}
                  getOptionLabel={(campaign) => `${campaign.name} (${campaign.subscribedEvents.length} Prospects)`}
                  renderInput={(params) => (
                    <>
                      <div className={classes.labelText} style={!user ? { color: '#686868' } : {}}>
                        Include Campaign Audiences
                      </div>
                      <TextField
                        {...params}
                        InputProps={{
                          style: {
                            fontWeight: 500,
                            paddingTop: '6.5px',
                            paddingBottom: '6.5px',
                          },
                          ...params.InputProps,
                        }}
                        variant="outlined"
                        placeholder="Campaign"
                      />
                      <div className={classes.helperText} style={!user ? { color: '#686868' } : {}}>
                        Select any active campaigns to include their prospects in this ad audience.
                      </div>
                    </>
                  )}
                />
              )}
            />

            <div className={classes.spacerSmall} />
            <DashboardSection sectionName="Build Your Ad" />

            <Grid container spacing={4} className={classes.buildLayout}>
              <Grid item xs={12} md={7}>
                <section className={classes.buildPanel} aria-labelledby="creative-section-title">
                  <div id="creative-section-title" className={classes.panelTitle}>1. Creative</div>
                  <div className={classes.panelDescription}>
                    Start with a Harvist design or use an image you already have.
                  </div>

                  <div className={classes.sourceChoices} role="radiogroup" aria-label="Ad creative source">
                    <ButtonBase
                      className={`${classes.sourceChoice} ${values.creativeSource === EAdCreativeSource.HarvistGenerated ? classes.sourceChoiceSelected : ''}`}
                      role="radio"
                      aria-checked={values.creativeSource === EAdCreativeSource.HarvistGenerated}
                      onClick={() => {
                        setFieldValue('creativeSource', EAdCreativeSource.HarvistGenerated, true);
                        setFieldValue('creativeFile', undefined, false);
                        setSelectedCreativeSource(EAdCreativeSource.HarvistGenerated);
                        setUploadedPreviewUrl(undefined);
                        setUploadError(undefined);
                        setUploadProgress(undefined);
                      }}
                    >
                      <ImageOutlinedIcon />
                      {values.creativeSource === EAdCreativeSource.HarvistGenerated && (
                        <CheckCircleOutlineIcon className={classes.selectedIcon} />
                      )}
                      <span className={classes.sourceTitle}>Use a Harvist design</span>
                      <span className={classes.sourceDescription}>Built from your marketing profile and kept up to date automatically.</span>
                    </ButtonBase>

                    <ButtonBase
                      className={`${classes.sourceChoice} ${values.creativeSource === EAdCreativeSource.UserUpload ? classes.sourceChoiceSelected : ''}`}
                      role="radio"
                      aria-checked={values.creativeSource === EAdCreativeSource.UserUpload}
                      onClick={() => {
                        setFieldValue('creativeSource', EAdCreativeSource.UserUpload, true);
                        setSelectedCreativeSource(EAdCreativeSource.UserUpload);
                      }}
                    >
                      <CloudUploadOutlinedIcon />
                      {values.creativeSource === EAdCreativeSource.UserUpload && (
                        <CheckCircleOutlineIcon className={classes.selectedIcon} />
                      )}
                      <span className={classes.sourceTitle}>Upload my own creative</span>
                      <span className={classes.sourceDescription}>
                        Upload your own custom image or video.
                      </span>
                    </ButtonBase>
                  </div>

                  {values.creativeSource === EAdCreativeSource.UserUpload && (
                    <AdCreativeUploader
                      mediaType={values.creativeMediaType}
                      value={values.creativeFile}
                      error={touched.creativeFile ? errors.creativeFile as string : undefined}
                      uploadProgress={uploadProgress}
                      uploadError={uploadError}
                      onMediaTypeChange={(mediaType) => {
                        setFieldValue('creativeMediaType', mediaType, true);
                        setFieldValue('creativeFile', undefined, false);
                        setUploadedPreviewUrl(undefined);
                      }}
                      onChange={(file) => {
                        setFieldValue('creativeFile', file, true);
                        setFieldTouched('creativeFile', true, false);
                        setUploadError(undefined);
                        setUploadProgress(undefined);
                      }}
                      onPreviewUrlChange={setUploadedPreviewUrl}
                      onRetry={() => handleSubmit()}
                    />
                  )}

                  <div className={classes.mobilePreview}>
                    <div className={classes.labelText}>Preview</div>
                    {renderPreview(values)}
                  </div>
                </section>

                <section className={classes.buildPanel} aria-labelledby="destination-section-title">
                  <div id="destination-section-title" className={classes.panelTitle}>2. Destination</div>
                  <div className={classes.panelDescription}>
                    Choose where someone goes when interacting with your digital ad.
                  </div>

                  <Field name="useCustomCallToActionLink">
                    {({ field }: FieldProps) => (
                      <div className={classes.destinationOptions} role="radiogroup" aria-label="Ad destination">
                        <FormControlLabel
                          control={(
                            <Radio
                              {...field}
                              checked={!field.value}
                              onChange={() => setFieldValue(field.name, false, true)}
                              icon={<FiberManualRecordIcon className={classes.radioUnchecked} />}
                              checkedIcon={<FiberManualRecordIcon className={classes.radioChecked} />}
                              classes={{ checked: classes.radio, root: classes.radioRoot }}
                            />
                          )}
                          classes={{ label: classes.label, root: classes.labelRoot }}
                          label="Harvist landing page"
                        />
                        <FormControlLabel
                          control={(
                            <Radio
                              {...field}
                              checked={field.value}
                              onChange={() => setFieldValue(field.name, true, true)}
                              icon={<FiberManualRecordIcon className={classes.radioUnchecked} />}
                              checkedIcon={<FiberManualRecordIcon className={classes.radioChecked} />}
                              classes={{ checked: classes.radio, root: classes.radioRoot }}
                            />
                          )}
                          classes={{ label: classes.label, root: classes.labelRoot }}
                          label="My own website or landing page"
                        />
                      </div>
                    )}
                  </Field>

                  {values.useCustomCallToActionLink ? (
                    <Field name="customCallToActionLink">
                      {({ field, meta }: FieldProps) => (
                        <CustomInputDashboard
                          labelText="Destination URL"
                          helperText="Enter your website or landing page URL."
                          inputProps={{
                            ...field,
                            placeholder: 'example.com',
                          }}
                          mask={(rawValue: string) => (
                            ['h', 't', 't', 'p', 's', ':', '/', '/', ...rawValue.split('').map(() => /./)]
                          )}
                          formControlProps={{ style: { marginBottom: 0 } }}
                          className={classes.settingInput}
                          error={meta.touched && !!meta.error}
                        />
                      )}
                    </Field>
                  ) : (
                    <div className={classes.landingPageInfo}>
                      The landing page uses your marketing profile, so the ad will wait if that profile is incomplete.
                      {' '}
                      <a
                        href={getAgentLandingPageSampleUrlFromType(EAgentLandingPageType.DigitalAd)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Preview the Harvist agent landing page
                      </a>
                      .
                    </div>
                  )}

                  {values.creativeSource === EAdCreativeSource.UserUpload
                    && values.useCustomCallToActionLink ? (
                      <div className={classes.landingPageInfo}>
                        This combination can launch without a completed Harvist marketing profile.
                      </div>
                    ) : null}
                </section>

                <section className={classes.buildPanel} aria-labelledby="budget-section-title">
                  <div id="budget-section-title" className={classes.panelTitle}>3. Budget and ad name</div>
                  <div className={classes.panelDescription}>
                    Set the monthly spend and give the campaign a private name for your dashboard.
                  </div>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Field name="name">
                        {({ field, meta }: FieldProps) => (
                          <CustomInputDashboard
                            helperText="Only you see this internal name."
                            labelText="Internal ad name"
                            inputProps={{
                              ...field,
                              placeholder: 'Example: Summer seller campaign',
                            }}
                            className={classes.settingInput}
                            error={meta.touched && !!meta.error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Field name="budget">
                        {({ field }: FieldProps) => (
                          <div className={classes.settingInput}>
                            <Box className={classes.labelText} display="flex" alignItems="center">
                              Monthly ad budget
                              <Tooltip
                                style={{ marginLeft: '10px' }}
                                placement="top"
                                title="Our ad partners recommend at least $300 per month for the best results"
                              >
                                <InfoOutlinedIcon aria-label="Budget recommendation" />
                              </Tooltip>
                            </Box>
                            <Slider
                              style={{ marginLeft: '10px' }}
                              valueLabelDisplay="off"
                              step={1000}
                              min={budgetMinimum}
                              max={budgetMaximum}
                              value={field.value}
                              onChange={(event, newValue) => setFieldValue(field.name, newValue, true)}
                              aria-label="Monthly ad budget"
                            />
                            <Typography variant="h5" color="primary">
                              <b>{`${formatCents(field.value, 0)} per month`}</b>
                            </Typography>
                            <div className={classes.renewalSummary}>
                              Your ad renews automatically by this amount each month while it is active. You can pause it from the dashboard.
                            </div>
                          </div>
                        )}
                      </Field>
                    </Grid>
                  </Grid>
                </section>
              </Grid>

              <Grid item md={5} className={classes.desktopPreview}>
                <aside className={`${classes.buildPanel} ${classes.previewPanel}`} aria-label="Ad preview">
                  <div className={classes.panelTitle}>Preview</div>
                  <div className={classes.panelDescription}>
                    This is the creative people will see. Select it to view a larger image.
                  </div>
                  {renderPreview(values)}
                </aside>
              </Grid>
            </Grid>

            <Button
              round
              color="primary"
              disabled={isSubmitting}
              type="submit"
              className={onSimpleViewSubmit ? classes.simpleViewNextButton : undefined}
            >
              {onSimpleViewSubmit ? 'Next' : 'Create My Ad'}
            </Button>
          </FormikOnError>
        </Form>
      )}
    </Formik>
  );
};
